import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomIcon, faRegularMoneyBillCircleInfo } from 'components/shared/CustomIcon';
import React, { useMemo } from 'react';
import { BodyText, Button, DangerText, Tooltip } from 'ui-toolkit';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { DisbursementStatusPill } from 'components/order/fees/table/status/DisbursementStatusPill';
import {
  IDisbursementFormData,
  isCheckDisbursementFormData,
  isWireDisbursementFormData,
} from 'types/order/escrow/IDisbursementFormData';
import { ICreateSettlementFeeForm } from 'types/order/fees/ICreateSettlementItemForm';
import { faCopy, faPaste } from '@fortawesome/pro-regular-svg-icons';
import { useDisbursementCopyContext } from '../../context/DisbursementCopyContext';
import { extractCopyableData, hasCopyableFields } from '../../utils/disbursementCopyUtils';
import { useClipboard } from '../hooks/useClipboard';
import { shouldShowCopyPasteOptions } from '../helpers/showCopyPasteOptions';

interface IDisbursementHeaderProps {
  feeName: string;
  data: IDisbursementFormData;
  disbursements: IDisbursementFormData[];
  fee: ICreateSettlementFeeForm;
  isReadOnly: boolean;
  isCollapsible: boolean;
  isCardCollapsed: boolean;
  showCopyPasteOptions: boolean;
  onRemove?: () => void;
  onToggleCollapse: () => void;
  onDelete: () => void;
  onPasteData?: () => void;
}

export const DisbursementHeader: React.FC<IDisbursementHeaderProps> = ({
  feeName,
  data,
  disbursements,
  fee,
  isReadOnly,
  isCollapsible,
  isCardCollapsed,
  showCopyPasteOptions,
  onRemove,
  onToggleCollapse,
  onDelete,
  onPasteData,
}) => {
  const { copyDisbursementData, copiedData, hasCopiedData } = useDisbursementCopyContext();
  const copyToClipboard = useClipboard();

  const handleCopyBeneficiaryInfo = () => {
    if (showCopyPasteOptions && (isCheckDisbursementFormData(data) || isWireDisbursementFormData(data))) {
      const copyableData = extractCopyableData(data);
      copyDisbursementData(copyableData);
    }
  };

  const handlePasteBeneficiaryInfo = () => {
    if (showCopyPasteOptions && copiedData && onPasteData) {
      onPasteData();
    }
  };

  const shouldRenderCopyPasteOptions = useMemo(() => {
    return shouldShowCopyPasteOptions({
      showCopyPasteOptions,
      isReadOnly,
      data,
      hasCopiedData,
    });
  }, [showCopyPasteOptions, isReadOnly, data, hasCopiedData]);

  return (
    <div className={'flex flex-row items-center gap-1'}>
      <div className={'flex-grow'}>
        <div className={'text-xl font-semibold'}>
          <span>Fee - {feeName} Disbursement</span>
        </div>
        {shouldRenderCopyPasteOptions && (
          <div className={'mt-1 flex gap-4 text-xl'}>
            {hasCopyableFields(data) && (
              <Button isInline intent={'ghost'} onClick={handleCopyBeneficiaryInfo}>
                <Tooltip placement="top" trigger="hover" tooltipContent={<BodyText>Copy Beneficiary Info</BodyText>}>
                  <FontAwesomeIcon icon={faCopy} />
                </Tooltip>
              </Button>
            )}

            <Button isInline intent={'ghost'} onClick={handlePasteBeneficiaryInfo} disabled={!hasCopiedData}>
              <Tooltip
                placement="top"
                trigger="hover"
                tooltipContent={
                  <BodyText>{hasCopiedData ? 'Paste Beneficiary Info' : 'No copied data available'}</BodyText>
                }
              >
                <FontAwesomeIcon icon={faPaste} className={classNames({ 'opacity-50': !hasCopiedData })} />
              </Tooltip>
            </Button>
          </div>
        )}
      </div>

      <div className={'flex flex-row items-center gap-3 text-xl'}>
        {!data.isNew && <DisbursementStatusPill disbursements={disbursements} onlyThisDisbursement={data} fee={fee} />}

        {!data.isNew && (
          <CustomIcon
            className={'cursor-pointer'}
            onClick={() => copyToClipboard(data.id)}
            icon={faRegularMoneyBillCircleInfo}
            title={`Disbursement ID: ${data.id}`}
            data-test-id={`id-icon-disbursement-${data.id}`}
          />
        )}

        {isCollapsible && (
          <ChevronDownIcon
            className={classNames('h-7 w-7 flex-none transition duration-150 ease-in-out', {
              '-rotate-180': !isCardCollapsed,
            })}
            onClick={onToggleCollapse}
          />
        )}

        {data.isNew && !isReadOnly && (
          <Button isInline intent={'ghost'} onClick={onRemove}>
            <DangerText>
              <FontAwesomeIcon icon={faTrashAlt} />
            </DangerText>
          </Button>
        )}

        {!data.isNew && !isReadOnly && (
          <div className={'basis-4'}>
            <Button isInline intent={'ghost'} onClick={onDelete} data-test-id={'trash-icon'}>
              <DangerText>
                <FontAwesomeIcon icon={faTrashAlt} />
              </DangerText>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
